import React, { useEffect, useState } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { stripePromise } from "@/lib/stripe";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Plus, Trash2, Loader2, Check, ShieldCheck } from "lucide-react";

const CARD_STYLE = {
  style: {
    base: {
      color: "#F4F7FF",
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontSize: "15px",
      "::placeholder": { color: "#64748B" },
      iconColor: "#22D3EE",
    },
    invalid: { color: "#FF4D4D", iconColor: "#FF4D4D" },
  },
};

function AddCardForm({ onAdded, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/setup-intent");
      const result = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) {
        toast.error(result.error.message);
      } else {
        const pmId = result.setupIntent.payment_method;
        await api.post("/payments/methods/default", { payment_method_id: pmId });
        toast.success("Card saved for one-tap booking!");
        onAdded();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-5 space-y-4" data-testid="add-card-form">
      <div className="bg-[#0B0E1B] border border-[#232A4D] rounded-xl px-4 py-3.5">
        <CardElement options={CARD_STYLE} />
      </div>
      <p className="text-xs text-slate-500 flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Test card: 4242 4242 4242 4242 · any future date · any CVC
      </p>
      <div className="flex gap-3">
        <button type="submit" disabled={loading || !stripe} data-testid="save-card-btn"
          className="btn-neon rounded-xl px-5 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save card
        </button>
        <button type="button" onClick={onCancel} className="rounded-xl px-5 py-2.5 text-sm text-slate-400 hover:text-white">Cancel</button>
      </div>
    </form>
  );
}

export default function WalletSection({ points }) {
  const [methods, setMethods] = useState([]);
  const [defaultPm, setDefaultPm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/payments/methods")
      .then((r) => { setMethods(r.data.methods); setDefaultPm(r.data.default); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    try { await api.delete(`/payments/methods/${id}`); toast.success("Card removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const makeDefault = async (id) => {
    try { await api.post("/payments/methods/default", { payment_method_id: id }); setDefaultPm(id); toast.success("Default card updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      {/* Points */}
      <div data-testid="points-balance-card" className="glass neon-border rounded-2xl p-6 flex items-center justify-between">
        <div>
          <p className="font-mono-caption text-xs uppercase tracking-[0.2em] text-cyan-300">Carivisor Points</p>
          <p className="font-heading text-4xl font-extrabold text-gradient-cool mt-1" data-testid="points-value">
            {(points ?? 0).toLocaleString()}
          </p>
          <p className="text-xs text-slate-400 mt-1">Worth ${((points ?? 0) / 100).toFixed(2)} · 100 pts = $1 at checkout</p>
        </div>
        <div className="w-16 h-16 rounded-2xl bg-grad-cool flex items-center justify-center text-white font-heading text-2xl font-black shadow-[0_0_26px_rgba(59,110,246,0.5)]">
          ★
        </div>
      </div>

      {/* Saved cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-cyan-300" /> Saved cards
          </h3>
          {!adding && (
            <button onClick={() => setAdding(true)} data-testid="add-card-btn"
              className="btn-cool rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Add card
            </button>
          )}
        </div>

        {adding && (
          <div className="mb-4">
            <Elements stripe={stripePromise}>
              <AddCardForm onAdded={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
            </Elements>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
        ) : methods.length === 0 && !adding ? (
          <div className="glass rounded-2xl p-8 text-center">
            <p className="text-slate-300 font-medium">No saved cards yet</p>
            <p className="text-slate-500 text-sm mt-1">Add a card to enable one-tap booking, just like the app.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {methods.map((m) => (
              <div key={m.id} data-testid={`card-row-${m.id}`} className="glass rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-8 rounded-md bg-grad-warm flex items-center justify-center text-white text-[10px] font-bold uppercase">
                    {m.brand}
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">•••• •••• •••• {m.last4}</p>
                    <p className="text-xs text-slate-500">Expires {String(m.exp_month).padStart(2, "0")}/{m.exp_year}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {defaultPm === m.id ? (
                    <span className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Default</span>
                  ) : (
                    <button onClick={() => makeDefault(m.id)} className="text-xs text-cyan-300 hover:underline">Make default</button>
                  )}
                  <button onClick={() => remove(m.id)} data-testid={`remove-card-${m.id}`} className="text-slate-400 hover:text-[#FF4D4D]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
